// A class that represents a date (year-month-day) in Eastern Time (America/New_York).
// - Stores only year, month, day (no time component).
//
// When reading from Postgres Date field, use ETDate.fromDateOnly()
// When writing to Postgres Date field, use .toDateOnly()
// To convert a Postgres timestamptz field to ETDate, use ETDate.fromDateTime()
// To get current date in ET, use ETDate.today()
//
// Note that this is a copy of the ETDate class in Fasten-Core

export class ETDate {
  constructor(year, month, day) {
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
      throw new TypeError('year, month, and day must be integers')
    }
    const d = new Date(Date.UTC(year, month - 1, day))
    if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
      throw new RangeError('Invalid date values')
    }

    this.year = year
    this.month = month
    this.day = day
  }

  /** Helper: extract Y/M/D from a Date interpreted in a given timezone using Intl. */
  static #extractYMDFromDateInTimeZone(date) {
    const timeZone = ETDate.TIME_ZONE
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date)

    const year = Number(parts.find(p => p.type === 'year')?.value)
    const month = Number(parts.find(p => p.type === 'month')?.value)
    const day = Number(parts.find(p => p.type === 'day')?.value)
    return { year, month, day }
  }

  /** Create from a JS Date — the Date is interpreted in Eastern Time */
  static fromDateTime(date) {
    if (date == null) return null
    const { year, month, day } = ETDate.#extractYMDFromDateInTimeZone(date)
    return new ETDate(year, month, day)
  }

  /** Create from a JS Date — but ignores the time component. */
  static fromDateOnly(date) {
    if (date == null) return null
    return new ETDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate())
  }

  /** Create from an ISO date string "YYYY-MM-DD" (interpreted as ET). */
  static fromISO(iso) {
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!m) throw new TypeError('Invalid ISO date (expected YYYY-MM-DD)')
    const [, y, mo, d] = m
    return new ETDate(Number(y), Number(mo), Number(d))
  }

  /** Today in Eastern Time */
  static today() {
    const now = new Date()
    const { year, month, day } = ETDate.#extractYMDFromDateInTimeZone(now)
    return new ETDate(year, month, day)
  }

  /** Return 'YYYY-MM-DD' */
  toISO() {
    const mm = String(this.month).padStart(2, '0')
    const dd = String(this.day).padStart(2, '0')
    return `${this.year}-${mm}-${dd}`
  }

  toString() {
    return this.toISO()
  }

  /** Equality */
  equals(other) {
    return this.year === other.year && this.month === other.month && this.day === other.day
  }

  /** Add days (positive or negative) — returns a new ETDate */
  addDays(days) {
    if (!Number.isFinite(days)) throw new TypeError('days must be a finite number')
    // Work with milliseconds in UTC and then extract the resulting Y/M/D in ET.
    // Start with a UTC timestamp for the current Y/M/D (safe anchor) and move by days*86400000 ms
    const anchorUtc = Date.UTC(this.year, this.month - 1, this.day)
    const resultTs = anchorUtc + Math.round(days) * 24 * 60 * 60 * 1000
    const resultDate = new Date(resultTs)
    const { year, month, day } = ETDate.fromDateOnly(resultDate)
    return new ETDate(year, month, day)
  }

  /** Add months (positive or negative) — returns a new ETDate.
   * If the target month has fewer days, the result is clamped to the last valid day.
   * Example: 2025-01-31 + 1 month => 2025-02-28
   */
  addMonths(months) {
    if (!Number.isFinite(months)) throw new TypeError('months must be a finite number')

    const totalMonths = this.year * 12 + (this.month - 1) + Math.trunc(months)
    const newYear = Math.floor(totalMonths / 12)
    const newMonth = (totalMonths % 12) + 1

    // Clamp day to last day of new month
    const lastDayOfNewMonth = new Date(Date.UTC(newYear, newMonth, 0)).getDate()
    const newDay = Math.min(this.day, lastDayOfNewMonth)

    return new ETDate(newYear, newMonth, newDay)
  }

  /** returns a new ETDate that is the first day of the current month */
  currentMonthStart() {
    return new ETDate(this.year, this.month, 1)
  }

  /** returns a new ETDate that is the last day of the current month */
  currentMonthEnd() {
    return new ETDate(this.year, this.month, 1).addMonths(1).addDays(-1)
  }

  /** returns a new ETDate that is the first day of the current quarter */
  currentQuarterStart() {
    const firstMonth = Math.trunc((this.month - 1) / 3) * 3 + 1
    return new ETDate(this.year, firstMonth, 1)
  }

  /** returns a new ETDate that is the last day of the current quarter */
  currentQuarterEnd() {
    return this.currentQuarterStart().addMonths(3).addDays(-1)
  }

  /** Compare: -1 if this < other, 0 if equal, 1 if this > other */
  compareTo(other) {
    if (this.year !== other.year) return this.year < other.year ? -1 : 1
    if (this.month !== other.month) return this.month < other.month ? -1 : 1
    if (this.day !== other.day) return this.day < other.day ? -1 : 1
    return 0
  }

  /**
   * Returns true if this date is strictly greater than (after) the other date.
   */
  greaterThan(other) {
    return this.compareTo(other) > 0
  }

  /**
   * Returns true if this date is strictly less than (before) the other date.
   */
  lessThan(other) {
    return this.compareTo(other) < 0
  }

  /**
   * Returns true if this date is greater than or equal to the other date.
   */
  greaterThanOrEqual(other) {
    return this.compareTo(other) >= 0
  }

  /**
   * Returns true if this date is less than or equal to the other date.
   */
  lessThanOrEqual(other) {
    return this.compareTo(other) <= 0
  }

  /**
   * Returns true if this date falls within the inclusive range [start, end].
   */
  between(startDate, endDate) {
    return this.compareTo(startDate) >= 0 && this.compareTo(endDate) <= 0
  }

  /**
   * Check if this ETDate is between start and end (inclusive).
   * If start is null, only the end is considered.
   * If end is null, only the start is considered.
   * If both are null, always returns true.
   */
  betweenNullables(start, end) {
    if (start && this.compareTo(start) < 0) return false
    if (end && this.compareTo(end) > 0) return false
    return true
  }

  /** ISO-friendly JSON representation */
  toJSON() {
    return this.toISO()
  }

  toDateOnly() {
    return new Date(Date.UTC(this.year, this.month - 1, this.day))
  }
}

// Static property for timezone
ETDate.TIME_ZONE = 'America/New_York'
